import { useRef, useState } from "react";
import { showToaster } from "../../../common/common";
import { useParams } from "react-router-dom";
import PostRequest from "../../../services/PostRequest";
import { McaDrawer } from "../../mca/Drawer";
import { BulkUploadResponseTable } from "../BulkUploadResponseTable";

export const AddBulkASIN = ({ show, handleClose, callback = () => {}, requestType = "amazon" }) => {
  const params = useParams();
  const [selectedFile, setSelectedFile] = useState(false);
  const [csvResponse, setCsvResponse] = useState(false);

  const ref = useRef(null);

  const isOfficeworks = requestType === "officeworks";
  const itemLabel = isOfficeworks ? "SKU" : "ASIN";

  const handleDownloadSampleCSV = () => {
    let sampleData;

    if (isOfficeworks) {
      sampleData = [
        ["sku", "customer_postcode", "store_id"],
        ["0119866", "2000", "0127"],
        ["0348540", "3000", "0001"],
      ];
    } else {
      sampleData = [
        ["domain", "customer_postcode", "asin", "include_raw_html"],
        ["amazon.com.au", "4500", "B08T7LSPHS", true],
        ["amazon.com.au", "4800", "B08T7LSPHR", false],
      ];
    }

    const csvContent = sampleData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", isOfficeworks ? "officeworks_sample.csv" : "sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleUploadCSV = async () => {
    if (!selectedFile) {
      showToaster("Please upload CSV File to process");
      return false;
    }

    const formData = new FormData();
    formData.append("csv", selectedFile);
    formData.append("collection_id", params.id);

    const options = {
      isMultiMedia: true,
      showLoader: "upload-csv",
    };

    const endpoint = isOfficeworks
      ? "/schedules/officeworks/sku/create/bulk/"
      : "/schedules/asin/create/bulk/";

    try {
      const response = await PostRequest(endpoint, formData, options);
      setCsvResponse(response);
      setSelectedFile(false);
      if (ref.current) ref.current.value = "";
      callback();
    } catch (error) {
      console.log(error.message);
      showToaster(error.message, "error");
    }
  };

  const handleOnChangeCSV = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const clearAndClose = () => {
    setCsvResponse(false);
    setSelectedFile(false);
    if (ref.current) ref.current.value = "";
    handleClose();
  };

  return (
    <McaDrawer
      open={show}
      onClose={clearAndClose}
      title={`Add Bulk ${itemLabel}`}
      subtitle="Upload a CSV to add up to 5000 requests at once"
      footer={
        <>
          <button
            type="button"
            className="mca-btn mca-btn--outline"
            onClick={clearAndClose}
          >
            Cancel
          </button>
          <button
            type="button"
            id="upload-csv"
            className="mca-btn mca-btn--primary"
            onClick={handleUploadCSV}
            disabled={!selectedFile}
          >
            Import
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
        <div className="mca-alert mca-alert--info">
          <svg
            className="lead"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5" />
            <path d="M12 8h.01" />
          </svg>
          <div className="mca-alert__body">
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>
              How to use bulk import
            </div>
            <p style={{ margin: "0 0 6px" }}>
              Use a Bulk Import CSV file to import up to 5000 requests into your
              Collection at once.
            </p>
            {isOfficeworks ? (
              <p style={{ margin: "0 0 6px" }}>
                Your CSV must contain columns: <strong>sku</strong>,{" "}
                <strong>customer_postcode</strong>, and <strong>store_id</strong>.
                Download the template for an example.
              </p>
            ) : (
              <p style={{ margin: "0 0 6px" }}>
                You can use <strong>any API request parameter</strong> in your
                Bulk Import CSV. The template gives examples, but you're free to
                add any API parameter name as another column in the Bulk Import
                CSV.
              </p>
            )}
            <p style={{ margin: 0 }}>
              <button
                type="button"
                onClick={handleDownloadSampleCSV}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--mca-primary)",
                  cursor: "pointer",
                  textDecoration: "underline",
                  fontSize: "inherit",
                  padding: 0,
                }}
              >
                Download template
              </button>{" "}
              an example Bulk Import CSV, then upload it below.
            </p>
          </div>
        </div>

        <div data-dropzone>
          <div className="mca-dropzone">
            <div className="mca-dropzone__icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 16V4" />
                <path d="m7 9 5-5 5 5" />
                <path d="M5 20h14" />
              </svg>
            </div>
            <div className="mca-dropzone__title">
              Drop your CSV here, or click to browse
            </div>
            <div className="mca-dropzone__hint">Up to 5000 rows</div>
            <input
              ref={ref}
              type="file"
              accept="text/csv"
              onChange={handleOnChangeCSV}
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
                top: 0,
                left: 0,
              }}
            />
          </div>
          {selectedFile && (
            <div className="mca-dropzone__file">
              <svg
                className="fic"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 2.5h8l4 4V21a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1Z" />
                <path d="m9 14 2 2 4-4" />
              </svg>
              <div style={{ flex: 1 }}>
                <div className="nm">{selectedFile.name}</div>
                <div className="mt">
                  {Math.round(selectedFile.size / 1024)} KB Â· ready to import
                </div>
              </div>
              <button
                type="button"
                className="mca-iconbtn"
                style={{ width: "32px", height: "32px" }}
                onClick={() => {
                  setSelectedFile(false);
                  if (ref.current) ref.current.value = "";
                }}
                aria-label="Remove"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {csvResponse && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span style={{ color: "var(--mca-success)" }}>
              Success Records : {csvResponse?.success_count}
            </span>
            <span style={{ color: "var(--mca-danger)" }}>
              Failed Records : {csvResponse?.error_rows.length}
            </span>
          </div>
        )}

        {csvResponse && csvResponse?.error_rows.length != 0 && (
          <BulkUploadResponseTable csvResponse={csvResponse.error_rows} />
        )}
      </div>
    </McaDrawer>
  );
};
